import React, { useEffect } from 'react'
import styled from 'particles/themes/styled'
import { User } from 'react-feather'
import { Text, ButtonProps, Box } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import ReactDOMServer from 'react-dom/server'

import { theme } from 'theme'
import { TagDetail } from 'state/partiposter'
import { formatBackendDate } from 'services/dates'
import { SEVERITY_COLOUR } from '.'
import ReactTooltip from 'react-tooltip'
import { useSelector } from 'react-redux'
import { selectSelectedCampaignSummary } from 'state/review'
import Tooltip from 'particles/Tooltip'
import { coreColors } from 'particles/colors'
import IconButton from 'particles/Button/IconButton'
import RichInfoTag, { TagState, TagContentType } from 'particles/Tag'

interface TagButtonProps extends ButtonProps {
    label: string
    number?: number
    key?: number
    tagDetail?: TagDetail
    state?: TagState
    contentType?: TagContentType
    maxWidth?: string
}

interface BaseTagButtonProps extends ButtonProps {
    key?: number
    tagDetail?: TagDetail
    severity?: string
    coloredTags?: boolean
}

const BaseTagButton = styled.button<BaseTagButtonProps>`
    font-family: inherit;
    font-size: 0.7em;
    background: ${props =>
        props.coloredTags && props.severity ? SEVERITY_COLOUR[props.severity] : theme.newColors.neutral50};
    color: ${props =>
        props.coloredTags
            ? theme.newColors.neutral50
            : props.disabled
              ? theme.newColors.neutral400
              : coreColors.blue300};
    border: 0.5px solid #e1e8fd;
    box-sizing: border-box;
    padding: 4px;
    box-shadow:
        0px 0px 1px rgba(0, 8, 172, 0.08),
        0px 0.5px 2px rgba(1, 12, 189, 0.16);
    border-radius: 6px;
    &:hover:not([disabled]) {
        cursor: pointer;
        background: ${props => (props.severity ? SEVERITY_COLOUR[props.severity] : coreColors.blue300)};
        color: ${coreColors.white50};
    }
    &:focus {
        outline: none;
    }
`

export const AddTagButton: React.FC<ButtonProps> = ({ onClick }) => {
    const [newT] = useNewTranslation()

    return (
        <Tooltip label={newT('button.addTag')} placement="top">
            <IconButton form="icon" size="xs" color="neutral" icon="plus" onClick={onClick} />
        </Tooltip>
    )
}

export const TagButton: React.FC<TagButtonProps> = ({
    tagDetail,
    label,
    number,
    state,
    contentType,
    maxWidth = '224px',
    ...props
}) => {
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)

    useEffect(() => {
        ReactTooltip.rebuild()
    })

    return (
        <RichInfoTag
            label={label}
            number={number}
            state={state}
            contentType={contentType}
            showTooltipOverflow={false}
            data-tip={
                tagDetail && selectedCampaignSummary
                    ? ReactDOMServer.renderToStaticMarkup(
                          <Box maxWidth="200px">
                              <Text fontSize="1.2em" color={theme.colors.astronaut}>
                                  <b>{tagDetail.brand_name}</b> - {tagDetail.campaign_title}
                              </Text>
                              <Text color={theme.newColors.neutral400} fontSize="1.5em" my={theme.spacing.xxxs}>
                                  {tagDetail.text ? tagDetail.text : ''}
                              </Text>
                              <Text color={theme.colors.doveGrey} fontSize="0.8em">
                                  {tagDetail.last_updater_user_info.name}
                              </Text>
                              <Text color={theme.colors.doveGrey} fontSize="0.8em">
                                  {formatBackendDate(tagDetail.updated_at, selectedCampaignSummary.country)}
                              </Text>
                          </Box>,
                      )
                    : ''
            }
            data-for="partiTooltip"
            maxWidth={maxWidth}
            {...props}
        />
    )
}

interface StyledProfileTagButtonProps extends ButtonProps {
    severity?: string
}

const StyledProfileTagButton = styled(BaseTagButton)<StyledProfileTagButtonProps>`
    color: ${theme.newColors.neutral50};
    background: ${(props: StyledProfileTagButtonProps) =>
        props.severity ? SEVERITY_COLOUR[props.severity] : coreColors.blue300};
`

export interface ProfileTagButtonProps extends StyledProfileTagButtonProps {
    key?: number
    severity?: string
    LeftIcon?: any
    RightIcon?: any
    disabled?: boolean
    children: any
}

export const ProfileTagButton: React.FC<ProfileTagButtonProps> = ({
    severity,
    LeftIcon = <User size="10px" />,
    RightIcon,
    children,
    disabled = true,
    ...props
}) => {
    return (
        <StyledProfileTagButton disabled={disabled} severity={severity} {...props}>
            {LeftIcon} {children} {RightIcon}
        </StyledProfileTagButton>
    )
}
